import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/apiClient';
import { Comment } from '../types/comment';
import { toast } from '../lib/toast';
import i18n from '../lib/i18n';

/** A failed post/reply shows as a toast; sign-in failures ask the viewer to sign in. */
export const notifyCommentFailed = (err: unknown) => {
  const code = (err as { code?: string } | null)?.code;
  toast.error(
    code === 'AUTH_REQUIRED' || code === 'TOKEN_INVALID'
      ? i18n.t('comments.sign_in_acv', 'Sign in with ACV to comment.')
      : i18n.t('comments.add_failed', 'Could not post the comment. Please try again.'),
  );
};

export const useComments = (videoId?: string) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchComments = useCallback(async () => {
    if (!videoId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await apiClient.listComments(videoId);
      
      if (error) throw error;
      
      setComments(data || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load comments');
      setComments([]); // Reset comments on error to prevent stale data
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  const addComment = async (content: string, timeSeconds?: number | null) => {
    if (!videoId) {
      setError('Video ID is required to comment');
      return null;
    }
    
    // For shared pages, we don't require user authentication
    // The apiClient will use the user context set in SharedVideoPage
    
    if (!content.trim()) {
      setError('Comment cannot be empty');
      return null;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await apiClient.addComment(videoId, content.trim(), null, timeSeconds);
        
      if (error) throw error;
      
      if (data) {
        // Enrich the optimistic comment with the user context already known
        // by the apiClient, so the author shows immediately without a reload.
        const { email } = apiClient.getUser();
        const knownName = (apiClient as any).authorName as string | null;
        const enriched = {
          ...data,
          authorEmail: (data as any).authorEmail || email || null,
          authorName : (data as any).authorName  || knownName || email || null,
          user       : { email: email || null },
        };
        setComments(prev => [enriched, ...prev]);
      }
      
      return data;
    } catch (err) {
      console.error('Error adding comment:', err);
      notifyCommentFailed(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteComment = async (commentId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const { error } = await apiClient.deleteComment(commentId);
        
      if (error) throw error;
      
      setComments(prev => prev.filter(c => c.id !== commentId));
      return true;
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete comment');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (videoId) {
      fetchComments();
    }
  }, [videoId]);

  /** Drops a comment from the list after it was deleted elsewhere (e.g. owner moderation). */
  const removeLocal = useCallback((commentId: string) => {
    setComments(prev => prev.filter(c => c.id !== commentId));
  }, []);

  return {
    comments,
    loading,
    error,
    addComment,
    deleteComment,
    removeLocal,
    refetch: fetchComments
  };
};
